import { ArrowRight, Clock } from 'lucide-react';
import type { CarouselItem } from '../../types';
import styles from './ProgramCard.module.css';

export interface ProgramCardProps {
  item: CarouselItem;
  onClick?: (item: CarouselItem) => void;
  /** Custom arrow image shown on hover (null = default arrow icon). */
  hoverArrowUrl?: string | null;
}

/** Tag pills come from metadata.tags; primary/secondary values render in the top row. */
function collectTags(item: CarouselItem): string[] {
  const tags: string[] = [];
  const metaTags = item.metadata?.tags;
  if (Array.isArray(metaTags)) {
    for (const tag of metaTags) {
      if (typeof tag === 'string' && tag.trim()) tags.push(tag);
    }
  }
  return tags.slice(0, 3);
}

export function ProgramCard({ item, onClick, hoverArrowUrl }: ProgramCardProps) {
  const tags = collectTags(item);

  const handleCardClick = () => onClick?.(item);

  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.card}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <span className={styles.accent} aria-hidden="true" />

      {(item.primaryValue || item.secondaryValue) && (
        <div className={styles.topRow}>
          {item.primaryValue && (
            <span className={styles.primaryBadge}>{item.primaryValue}</span>
          )}
          {item.secondaryValue && (
            <span className={styles.secondaryValue}>
              <Clock size={13} strokeWidth={2} aria-hidden="true" />
              {item.secondaryValue}
            </span>
          )}
        </div>
      )}

      {item.badge && <div className={styles.tag}>{item.badge}</div>}

      <h3 className={styles.title}>{item.title}</h3>

      {tags.length > 0 && (
        <div className={styles.pills}>
          {tags.map((tag, index) => (
            <span key={`${tag}-${index}`} className={styles.pill}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {item.description && (
        <p className={styles.description}>{item.description}</p>
      )}

      {item.linkUrl && (
        <div className={styles.footer}>
          <span className={styles.cornerArrow} aria-hidden="true">
            {hoverArrowUrl ? (
              <img src={hoverArrowUrl} alt="" className={styles.cornerArrowImage} />
            ) : (
              <ArrowRight size={18} strokeWidth={2.5} />
            )}
          </span>
        </div>
      )}
    </div>
  );
}
